import React, { useState, useEffect } from 'react';
import { 
  FaUser, FaLock, FaPhone, FaBell, FaShieldAlt, FaTrash, 
  FaExclamationTriangle, FaCheckCircle, FaEnvelope, FaMapMarkedAlt 
} from 'react-icons/fa';

const Settings = () => {
  const [user, setUser] = useState({
    email: 'user@example.com',
    firstName: 'Jan',
    lastName: 'Kowalski',
    phoneNumber: '+48 123 456 789',
    street: 'ul. Przykładowa 123',
    city: 'Warszawa',
    postalCode: '00-001',
    country: 'Polska',
    isEmailVerified: false,
    isPhoneVerified: false,
  });

  const [activeTab, setActiveTab] = useState('userData');
  const [formData, setFormData] = useState({ ...user });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [verificationData, setVerificationData] = useState({
    showModal: false,
    type: '',
    step: 1,
    code1: '',
    code2: '',
    tempValue: '',
  });

  // Stan dla powiadomień
  const [notifications, setNotifications] = useState({
    email: true,
    sms: false,
    push: true,
    newsletter: true,
  });

  // Stan dla prywatności
  const [privacy, setPrivacy] = useState({
    profileVisibility: 'public',
    dataSharing: false,
    personalizedAds: true,
  });

  useEffect(() => {
    if (alert.show) {
      const timer = setTimeout(() => {
        setAlert({ ...alert, show: false });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName) {
      showAlert('Wypełnij wszystkie wymagane pola', 'error');
      return;
    }
    setIsSaving(true);
    await simulateAPI();
    setUser(formData);
    setIsSaving(false);
    showAlert('Dane zostały zaktualizowane', 'success');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showAlert('Hasła nie są identyczne', 'error');
      return;
    }
    if (!isStrongPassword(passwordForm.newPassword)) {
      showAlert('Hasło musi zawierać min. 8 znaków, w tym wielką literę, cyfrę i znak specjalny', 'error');
      return;
    }
    startVerificationProcess('password');
  };

  const startVerificationProcess = (type) => {
    setVerificationData({
      showModal: true,
      type,
      step: 1,
      code1: '',
      code2: '',
      tempValue: type === 'email' ? formData.email : type === 'phone' ? formData.phoneNumber : '',
    });
  };

  const handleVerification = async () => {
    if (verificationData.step === 1) {
      // Symulacja wysyłki kodów
      await simulateAPI();
      setVerificationData({ ...verificationData, step: 2 });
    } else {
      // Symulacja weryfikacji
      await simulateAPI();
      if (verificationData.type === 'email') {
        setUser({ ...user, isEmailVerified: true });
      }
      if (verificationData.type === 'phone') {
        setUser({ ...user, isPhoneVerified: true });
      }
      if (verificationData.type === 'password') {
        showAlert('Hasło zostało zmienione', 'success');
        setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
      }
      closeVerificationModal();
    }
  };

  const closeVerificationModal = () => {
    setVerificationData({
      showModal: false,
      type: '',
      step: 1,
      code1: '',
      code2: '',
      tempValue: '',
    });
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
  };

  const simulateAPI = () => new Promise(resolve => setTimeout(resolve, 1000));

  const isStrongPassword = (password) => {
    const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+])[A-Za-z\d!@#$%^&*()_+]{8,}$/;
    return regex.test(password);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Alerty */}
      {alert.show && (
        <div className={`fixed bottom-4 right-4 p-4 rounded-md shadow-lg flex items-center 
          ${alert.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {alert.type === 'success' ? (
            <FaCheckCircle className="w-5 h-5 mr-2 text-green-600" />
          ) : (
            <FaExclamationTriangle className="w-5 h-5 mr-2 text-red-600" />
          )}
          <span className="text-sm">{alert.message}</span>
        </div>
      )}

      {/* Nagłówek */}
      <div className="pb-6 border-b border-gray-200">
        <h1 className="text-3xl font-bold text-gray-900">Ustawienia konta</h1>
      </div>

      <div className="mt-8 lg:grid lg:grid-cols-12 lg:gap-8">
        {/* Panel nawigacji */}
        <aside className="lg:col-span-3 mb-8 lg:mb-0">
          <nav className="space-y-1">
            {[
              { name: 'Dane użytkownika', icon: FaUser, tab: 'userData' },
              { name: 'Bezpieczeństwo', icon: FaLock, tab: 'accountSettings' },
              { name: 'Powiadomienia', icon: FaBell, tab: 'notifications' },
              { name: 'Prywatność', icon: FaShieldAlt, tab: 'privacy' },
            ].map((item) => (
              <button
                key={item.name}
                onClick={() => setActiveTab(item.tab)}
                className={`w-full group flex items-center px-4 py-3 rounded-lg transition-colors
                  ${activeTab === item.tab 
                    ? 'bg-green-50 text-green-700'
                    : 'text-gray-600 hover:bg-gray-50'}`}
              >
                <item.icon className={`mr-3 flex-shrink-0 ${activeTab === item.tab ? 'text-green-500' : 'text-gray-400'}`} />
                {item.name}
              </button>
            ))}
          </nav>
        </aside>

        {/* Główna zawartość */}
        <main className="lg:col-span-9 space-y-8">
          {/* Sekcja danych użytkownika */}
          {activeTab === 'userData' && (
            <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6">
              <div className="mb-8">
                <h2 className="text-xl font-semibold flex items-center">
                  <FaUser className="mr-2 text-green-600" />
                  Profil użytkownika
                </h2>
                <p className="mt-1 text-sm text-gray-500">Zarządzaj swoimi podstawowymi informacjami</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Imię *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nazwisko *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Adres email</label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={formData.email}
                      disabled
                      className="flex-1 px-4 py-2 bg-gray-100 border border-gray-300 rounded-md cursor-not-allowed"
                    />
                    <button
                      type="button"
                      onClick={() => startVerificationProcess('email')}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                      Weryfikuj
                    </button>
                  </div>
                  {user.isEmailVerified && (
                    <span className="text-sm text-green-600 mt-1 flex items-center">
                      <FaCheckCircle className="mr-1" /> Zweryfikowany
                    </span>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Numer telefonu</label>
                  <div className="flex gap-2">
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                    <button
                      type="button"
                      onClick={() => startVerificationProcess('phone')}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                      Weryfikuj
                    </button>
                  </div>
                  {user.isPhoneVerified && (
                    <span className="text-sm text-green-600 mt-1 flex items-center">
                      <FaCheckCircle className="mr-1" /> Zweryfikowany
                    </span>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ulica</label>
                  <input
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Miasto</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kod pocztowy</label>
                  <input
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kraj</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end">
                <button
                  type="submit"
                  disabled={isSaving}
                  className={`inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-white 
                    ${isSaving ? 'bg-green-400' : 'bg-green-600 hover:bg-green-700'} 
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500`}
                >
                  {isSaving ? 'Zapisywanie...' : 'Zapisz zmiany'}
                </button>
              </div>
            </form>
          )}

          {/* Sekcja bezpieczeństwa */}
          {activeTab === 'accountSettings' && (
            <div className="space-y-8">
              <form onSubmit={handlePasswordSubmit} className="bg-white shadow rounded-lg p-6">
                <div className="mb-8">
                  <h2 className="text-xl font-semibold flex items-center">
                    <FaLock className="mr-2 text-green-600" />
                    Zmiana hasła
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">Zaktualizuj swoje hasło dostępu</p>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Obecne hasło</label>
                    <input
                      type="password"
                      name="oldPassword"
                      value={passwordForm.oldPassword}
                      onChange={handlePasswordChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nowe hasło</label>
                    <input
                      type="password"
                      name="newPassword"
                      value={passwordForm.newPassword}
                      onChange={handlePasswordChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Potwierdź nowe hasło</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={passwordForm.confirmPassword}
                      onChange={handlePasswordChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end">
                  <button
                    type="submit"
                    className="inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
                  >
                    Zmień hasło
                  </button>
                </div>
              </form>

              {/* Sekcja niebezpieczna */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex items-start">
                  <FaExclamationTriangle className="flex-shrink-0 h-6 w-6 text-red-400 mr-3" />
                  <div>
                    <h3 className="text-lg font-medium text-red-800">Niebezpieczna strefa</h3>
                    <p className="mt-1 text-sm text-red-700">
                      Usunięcie konta jest nieodwracalne. Wszystkie Twoje dane zostaną trwale usunięte.
                    </p>
                    <div className="mt-4">
                      <button
                        onClick={() => setShowDeleteModal(true)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200"
                      >
                        <FaTrash className="mr-2 h-4 w-4" />
                        Usuń konto
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sekcja powiadomień */}
          {activeTab === 'notifications' && (
            <form className="bg-white shadow rounded-lg p-6">
              <div className="mb-8">
                <h2 className="text-xl font-semibold flex items-center">
                  <FaBell className="mr-2 text-green-600" />
                  Ustawienia powiadomień
                </h2>
                <p className="mt-1 text-sm text-gray-500">Dostosuj sposób otrzymywania powiadomień</p>
              </div>

              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Powiadomienia email</h3>
                    <p className="text-sm text-gray-500">Ważne aktualizacje i informacje</p>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={notifications.email}
                      onChange={(e) => setNotifications({ ...notifications, email: e.target.checked })}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Powiadomienia SMS</h3>
                    <p className="text-sm text-gray-500">Pilne informacje i alerty</p>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={notifications.sms}
                      onChange={(e) => setNotifications({ ...notifications, sms: e.target.checked })}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Newsletter</h3>
                    <p className="text-sm text-gray-500">Promocje i aktualności</p>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={notifications.newsletter}
                      onChange={(e) => setNotifications({ ...notifications, newsletter: e.target.checked })}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end">
                <button
                  type="submit"
                  className="inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
                >
                  Zapisz zmiany
                </button>
              </div>
            </form>
          )}

          {/* Sekcja prywatności */}
          {activeTab === 'privacy' && (
            <form className="bg-white shadow rounded-lg p-6">
              <div className="mb-8">
                <h2 className="text-xl font-semibold flex items-center">
                  <FaShieldAlt className="mr-2 text-green-600" />
                  Ustawienia prywatności
                </h2>
                <p className="mt-1 text-sm text-gray-500">Zarządzaj swoją prywatnością i udostępnianiem danych</p>
              </div>

              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Profil publiczny</h3>
                    <p className="text-sm text-gray-500">Udostępniaj swój profil innym użytkownikom</p>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={privacy.profileVisibility === 'public'}
                      onChange={(e) => setPrivacy({ ...privacy, profileVisibility: e.target.checked ? 'public' : 'private' })}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Udostępnianie danych</h3>
                    <p className="text-sm text-gray-500">Zgoda na udostępnianie danych partnerom</p>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={privacy.dataSharing}
                      onChange={(e) => setPrivacy({ ...privacy, dataSharing: e.target.checked })}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Spersonalizowane reklamy</h3>
                    <p className="text-sm text-gray-500">Dopasowane rekomendacje i oferty</p>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={privacy.personalizedAds}
                      onChange={(e) => setPrivacy({ ...privacy, personalizedAds: e.target.checked })}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end">
                <button
                  type="submit"
                  className="inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
                >
                  Zapisz zmiany
                </button>
              </div>
            </form>
          )}

          {/* Modale */}
          {verificationData.showModal && (
            <div className="fixed z-10 inset-0 overflow-y-auto">
              <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>

                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                      <FaLock className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {verificationData.step === 1 ? 'Weryfikacja dwuetapowa' : 'Wprowadź kody'}
                      </h3>
                      <div className="mt-2">
                        {verificationData.step === 1 ? (
                          <p className="text-sm text-gray-500">
                            Na Twój adres email i numer telefonu zostały wysłane kody weryfikacyjne.
                          </p>
                        ) : (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Kod z email</label>
                              <input
                                type="text"
                                value={verificationData.code1}
                                onChange={(e) => setVerificationData({ ...verificationData, code1: e.target.value })}
                                className="w-full px-4 py-2 border rounded-md"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Kod z SMS</label>
                              <input
                                type="text"
                                value={verificationData.code2}
                                onChange={(e) => setVerificationData({ ...verificationData, code2: e.target.value })}
                                className="w-full px-4 py-2 border rounded-md"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      onClick={handleVerification}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      {verificationData.step === 1 ? 'Wyślij kody' : 'Zweryfikuj'}
                    </button>
                    <button
                      onClick={closeVerificationModal}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:w-auto sm:text-sm"
                    >
                      Anuluj
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Modal usuwania konta */}
          {showDeleteModal && (
            <div className="fixed z-10 inset-0 overflow-y-auto">
              <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>

                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                      <FaExclamationTriangle className="h-6 w-6 text-red-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">Usunięcie konta</h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Czy na pewno chcesz usunąć swoje konto? Tej operacji nie można cofnąć.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      onClick={() => {
                        // Tutaj logika usuwania konta
                        setShowDeleteModal(false);
                      }}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      Usuń konto
                    </button>
                    <button
                      onClick={() => setShowDeleteModal(false)}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:w-auto sm:text-sm"
                    >
                      Anuluj
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      <style jsx>{`
        .switch {
          position: relative;
          display: inline-block;
          width: 60px;
          height: 34px;
        }

        .switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: .4s;
          border-radius: 34px;
        }

        .slider:before {
          position: absolute;
          content: "";
          height: 26px;
          width: 26px;
          left: 4px;
          bottom: 4px;
          background-color: white;
          transition: .4s;
          border-radius: 50%;
        }

        input:checked + .slider {
          background-color: #10B981;
        }

        input:checked + .slider:before {
          transform: translateX(26px);
        }
      `}</style>
    </div>
  );
};

export default Settings;